import { DAY_KEYS, type Closure, type DayKey, type FirstSlotConfig, type RestaurantIdentity, type WeeklyHours } from '@/types/restaurant';
import { slotPeriod, SPLIT_HOUR } from '@/features/menu/servicePeriod';
import { localParts } from '@/lib/hours';
import type { MenuItem } from '@/types/menu';

const DEFAULT_STEP_MINUTES = 10;
// DF-0330 — default when no restaurant timezone is supplied: the RUNNING
// environment's own tz. On the same machine this reads the wall clock exactly
// like the old `now.getHours()`/`now.getDay()` calls did, so callers that
// don't yet resolve a restaurant timezone see no behavior change; real
// callers pass the restaurant's own tz (see `restaurantTz` in `@/lib/hours`).
const SYSTEM_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function parseHm(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}

function formatMinutes(total: number): string {
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
}

/** SC-1465 — the first-slot setting of the cart's mode (delivery vs takeaway).
 *  `null` when the identity carries none: the slot math then runs exactly as
 *  before the setting existed. */
export function firstSlotFor(
  identity: Pick<RestaurantIdentity, 'firstSlotEmporter' | 'firstSlotLivraison'> | null | undefined,
  mode: string | null | undefined,
): FirstSlotConfig | null {
  if (!identity) return null;
  return (mode === 'delivery' ? identity.firstSlotLivraison : identity.firstSlotEmporter) ?? null;
}

/** SC-1500 — l'option « Dès que possible » est-elle offerte pour ce mode ?
 *  Emporter / livraison seulement ; sur place, réglage absent ou identité
 *  inconnue : non. */
export function asapEnabledFor(
  identity: Pick<RestaurantIdentity, 'asapEmporter' | 'asapLivraison'> | null | undefined,
  mode: string | null | undefined,
): boolean {
  if (!identity) return false;
  if (mode === 'delivery') return identity.asapLivraison === true;
  if (mode === 'takeaway' || mode === 'take_out') return identity.asapEmporter === true;
  return false;
}

/** Where a range's slots start, prep already counted.
 *
 *  - `auto` (or no setting, or a blank cell): `open + prepMinutes`, the rule
 *    from before SC-1465.
 *  - `manual` with a time for this day and service (lunch = range opening
 *    before SPLIT_HOUR, dinner = after — same split as `slotPeriod`): that
 *    time, never earlier than `open` (sale_api only accepts `open <= t`).
 *  Today's caller still applies `now + prep` on top. */
function rangeStart(
  open: number,
  prepMinutes: number,
  day: DayKey,
  firstSlot: FirstSlotConfig | null,
): number {
  if (firstSlot?.mode === 'manual') {
    const cell = firstSlot.days[day]?.[open < SPLIT_HOUR * 60 ? 'lunch' : 'dinner'];
    if (typeof cell === 'string' && /^\d{2}:\d{2}$/.test(cell)) return Math.max(parseHm(cell), open);
  }
  return open + prepMinutes;
}

/** Same-day pickup/delivery slots for a checkout flow.
 *
 *  - Earliest slot is the exact `max(now, open) + prepMinutes`, NOT snapped up
 *    to the step: snapping made the customer wait up to a full step longer than
 *    the prep time the restaurant configured (12:05 + 20 min was offered as
 *    12:30 instead of 12:25). It is the only off-grid slot (SC-568).
 *  - One slot every `stepMinutes` (default 10) after that.
 *  - Latest slot is the last step strictly before `close`. Prep time is applied
 *    ONCE (on the earliest side): the kitchen cooks the final order during the
 *    last prep window before closing — we do not pull the last slot back by prep
 *    a second time. Mirrors sale_api's validate_scheduled_at, which accepts a
 *    pickup `t` with `open <= t < close` and `t >= now + prep`.
 *  - Slots only inside the day's open ranges from `hours`.
 *  `now` is meant to be a LIVE value, not a mount-time snapshot: the caller
 *  re-runs this as the clock advances so the earliest slot leaves the picker
 *  once it expires, instead of being rejected at submit (SC-568).
 *  Returns an empty array if today is closed, is a closure day, or if `now`
 *  is already too late for any range to fit prep time.
 */
export function todayCheckoutSlots(
  hours: WeeklyHours,
  now: Date,
  prepMinutes: number,
  stepMinutes: number = DEFAULT_STEP_MINUTES,
  closures: Closure[] = [],
  tz: string = SYSTEM_TZ,
  firstSlot: FirstSlotConfig | null = null,
): string[] {
  // DF-0330 — "today" and "now" must read as the RESTAURANT sees them, not the
  // customer's browser: a Cayenne restaurant at 20:00 local is already the next
  // calendar day for a browser on Paris time.
  const p = localParts(now, tz);
  // Early-return if today falls inside a closure (uses restaurant-local date components)
  if (closures.length > 0) {
    const todayIso = `${p.year}-${pad(p.month)}-${pad(p.day)}`;
    for (const c of closures) {
      if (typeof c?.from === 'string' && typeof c?.to === 'string' && c.from <= todayIso && todayIso <= c.to) {
        return [];
      }
    }
  }
  const day = DAY_KEYS[p.weekday];
  const ranges = hours[day] ?? [];
  if (ranges.length === 0) return [];

  // Seconds count against the kitchen: at 12:05:30 the earliest honest pickup is
  // 12:25:30, so the current minute is rounded up before prep is added — an
  // off-grid earliest slot must never promise less prep time than configured.
  const nowM = p.hh * 60 + p.mm + (now.getSeconds() > 0 ? 1 : 0);
  const earliest = nowM + prepMinutes;

  const slots: string[] = [];
  for (const range of ranges) {
    const open = parseHm(range.open);
    const close = parseHm(range.close);
    if (close <= open) continue;
    const start = Math.max(earliest, rangeStart(open, prepMinutes, day, firstSlot));
    // Pickup must be strictly before close (close itself is out-of-hours on the
    // backend, which gates on open <= t < close); prep is already counted once in
    // `start`, so we do not pull the last slot back by prep a second time.
    pushRangeSlots(slots, start, close, stepMinutes);
  }
  return slots;
}

/** Slots of one open range, from `start` (prep already counted) to strictly
 *  before `close`: the off-grid "au plus tôt" head when the grid would push it
 *  later, then one slot every `stepMinutes`. Nothing when `start >= close`. */
function pushRangeSlots(slots: string[], start: number, close: number, stepMinutes: number): void {
  if (start >= close) return;
  const firstOnGrid = Math.ceil(start / stepMinutes) * stepMinutes;
  // The "au plus tôt" head: kept only when the grid would push it later.
  if (start < firstOnGrid) slots.push(formatMinutes(start));
  for (let m = firstOnGrid; m < close; m += stepMinutes) {
    slots.push(formatMinutes(m));
  }
}


/** Pickup/delivery slots for a FUTURE day (J+`dayOffset` pre-commande),
 *  `dayOffset` >= 1.
 *
 *  No prep-time offset from `now`, but the earliest slot of each range is
 *  `open + prepMinutes`, exactly as for today: the kitchen starts cooking when
 *  it opens, not the night before — a 09:00 opening with 20 min of prep offers
 *  09:20 on every day, not 09:00 from tomorrow on (SC-1435). Then the regular
 *  step grid up to `close` for that day's weekday. Returns [] if the day is
 *  closed or falls inside a closure.
 */
export function dayCheckoutSlots(
  hours: WeeklyHours,
  now: Date,
  dayOffset: number,
  stepMinutes: number = DEFAULT_STEP_MINUTES,
  closures: Closure[] = [],
  tz: string = SYSTEM_TZ,
  prepMinutes: number = 0,
  firstSlot: FirstSlotConfig | null = null,
): string[] {
  // DF-0330 — the target day is the restaurant's Nth-next calendar day, not
  // the browser's: incrementing via `Date.UTC` on the restaurant-local Y-M-D
  // normalizes month/year rollover without depending on any wall clock.
  const p = localParts(now, tz);
  const target = new Date(Date.UTC(p.year, p.month - 1, p.day + dayOffset));
  const ty = target.getUTCFullYear();
  const tm = target.getUTCMonth() + 1;
  const td = target.getUTCDate();

  // Closure check for the target day
  if (closures.length > 0) {
    const targetIso = `${ty}-${pad(tm)}-${pad(td)}`;
    for (const c of closures) {
      if (typeof c?.from === 'string' && typeof c?.to === 'string' && c.from <= targetIso && targetIso <= c.to) {
        return [];
      }
    }
  }

  // getUTCDay(): Sun=0..Sat=6 → DAY_KEYS index: Mon=0..Sun=6 (pure calendar
  // arithmetic — the weekday of a Y-M-D doesn't depend on a timezone).
  const targetWeekday = (target.getUTCDay() + 6) % 7;
  const day = DAY_KEYS[targetWeekday];
  const ranges = hours[day] ?? [];
  if (ranges.length === 0) return [];

  const slots: string[] = [];
  for (const range of ranges) {
    const open = parseHm(range.open);
    const close = parseHm(range.close);
    if (close <= open) continue;
    pushRangeSlots(slots, rangeStart(open, prepMinutes, day, firstSlot), close, stepMinutes);
  }
  return slots;
}

/** Pickup/delivery slots for TOMORROW (J+1 pre-commande) — thin wrapper over
 *  `dayCheckoutSlots` kept so existing callers/tests don't need to change.
 */
export function tomorrowCheckoutSlots(
  hours: WeeklyHours,
  now: Date,
  stepMinutes: number = DEFAULT_STEP_MINUTES,
  closures: Closure[] = [],
  tz: string = SYSTEM_TZ,
  prepMinutes: number = 0,
  firstSlot: FirstSlotConfig | null = null,
): string[] {
  return dayCheckoutSlots(hours, now, 1, stepMinutes, closures, tz, prepMinutes, firstSlot);
}

/** Pre-checkout slot diagnosis for the cart drawer: tells the customer BEFORE
 *  entering the checkout why no pickup time will be available, instead of
 *  letting them discover "Aucun créneau" (or a backend reject) at payment.
 *
 *  - 'closed'   — the restaurant itself has no orderable slot left today
 *                 (closed day, closure, or past last order time).
 *  - 'conflict' — slots exist, but the cart's service-period restrictions
 *                 (midi-only / soir-only products) filter them all out.
 *                 `blockingIds` names the offending products.
 */
export type CartSlotDiagnosis =
  | { kind: 'ok'; slots: string[] }
  | { kind: 'closed' }
  | { kind: 'conflict'; blockingIds: string[] };

export function diagnoseCartSlots(
  hours: WeeklyHours,
  now: Date,
  prepMinutes: number,
  closures: Closure[],
  lines: { itemId: string }[],
  items: Pick<MenuItem, 'id' | 'active_lunch' | 'active_dinner'>[],
  tz?: string,
  firstSlot: FirstSlotConfig | null = null,
): CartSlotDiagnosis {
  const daySlots = todayCheckoutSlots(hours, now, prepMinutes, undefined, closures, tz, firstSlot);
  if (daySlots.length === 0) return { kind: 'closed' };
  const filtered = filterSlotsByCartAvailability(daySlots, lines, items);
  if (filtered.length > 0) return { kind: 'ok', slots: filtered };
  const remaining = new Set(daySlots.map(slotPeriod));
  const blockingIds: string[] = [];
  for (const { itemId } of lines) {
    const item = items.find((it) => it.id === itemId);
    if (!item) continue;
    const blocks =
      (remaining.has('lunch') && !item.active_lunch) ||
      (remaining.has('dinner') && !item.active_dinner);
    if (blocks && !blockingIds.includes(itemId)) blockingIds.push(itemId);
  }
  return { kind: 'conflict', blockingIds };
}

/** Restrict same-day checkout slots to the service periods in which *every*
 *  cart product is available. A midi-only product (`active_dinner: false`)
 *  removes all evening slots; a soir-only product removes all midday slots; a
 *  cart mixing the two yields no slots. Cart lines whose product is absent from
 *  `items` don't constrain the result — mirrors the checkout price-sync lookup,
 *  which skips lines missing from the menu snapshot.
 */
export function filterSlotsByCartAvailability(
  slots: string[],
  lines: { itemId: string }[],
  items: Pick<MenuItem, 'id' | 'active_lunch' | 'active_dinner'>[],
): string[] {
  let lunchOk = true;
  let dinnerOk = true;
  for (const { itemId } of lines) {
    const item = items.find((it) => it.id === itemId);
    if (!item) continue;
    if (!item.active_lunch) lunchOk = false;
    if (!item.active_dinner) dinnerOk = false;
  }
  if (lunchOk && dinnerOk) return slots;
  return slots.filter((s) => (slotPeriod(s) === 'lunch' ? lunchOk : dinnerOk));
}

/** L'instant `iso` tombe-t-il le lendemain de `now` ?
 *
 *  Sert aux ecrans qui n'affichent qu'une heure (« Retrait a 12:30 ») et qui
 *  doivent dire « demain » des que la commande est une pre-commande J+1 :
 *  l'heure nue se lit comme « tout a l'heure ». Compare des jours civils
 *  locaux, jamais un ecart de 24 h — 23:00 et 01:00 sont deux jours a deux
 *  heures d'intervalle.
 */
export function isNextDay(iso: string, now: Date = new Date()): boolean {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return false;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    at.getFullYear() === tomorrow.getFullYear()
    && at.getMonth() === tomorrow.getMonth()
    && at.getDate() === tomorrow.getDate()
  );
}
